import { type Capability, type Feature, decide, isEntitled } from '@emr/shared';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useGrants, useSession } from '../auth/sessionContext.ts';
import { PageHeading, Panel } from './primitives.tsx';

/**
 * The route-level half of capability/entitlement gating, factored out of `routes/GatedPages.tsx` so
 * `routes/patients/PatientListPage.tsx` and `PatientChartPage.tsx` can reuse it rather than writing
 * a second, "equivalent" gate — `claude-kit/rules/invariants.md`, "Favor the general, reusable
 * solution once a pattern actually recurs": this is the second and third caller of the exact same
 * shape `GatedPages.tsx` first wrote for Analytics/Audit.
 *
 * A nav item can be hidden (`shell/navigation.ts`); a route cannot rely on that alone, because a URL
 * can be typed directly. So every gated route re-applies the same `@emr/shared` functions that
 * produced the nav decision, per `invariants.md`'s "Centralize the actual authorization decision,
 * not just the default" — never a second, re-derived role check.
 *
 * The two refusals are worded differently on purpose, and `shared/src/authorization.ts` states why
 * in its own header: a user who lacks a capability by role must never be told to upgrade, because
 * the upgrade would not grant it and the message misdirects a practice into paying for the wrong
 * fix. The converse sends an administrator hunting for a role change that does not exist.
 */

export function PermissionRefusal({ capability }: { readonly capability: Capability }) {
  const { t } = useTranslation('common');
  return (
    <Panel title={t('gatedRoute.permissionRefusal.title')}>
      <p className="m-0 text-sm text-ink">
        <Trans
          t={t}
          i18nKey="gatedRoute.permissionRefusal.body"
          values={{ capability }}
          components={{ code: <code className="font-mono" /> }}
        />
      </p>
      <p className="m-0 mt-2 text-sm text-ink-muted">
        {t('gatedRoute.permissionRefusal.roleHint')}
      </p>
    </Panel>
  );
}

export function UpgradeRefusal({ feature }: { readonly feature: Feature }) {
  const { t } = useTranslation('common');
  return (
    <Panel title={t('gatedRoute.upgradeRefusal.title')}>
      <p className="m-0 text-sm text-ink">
        <Trans
          t={t}
          i18nKey="gatedRoute.upgradeRefusal.body"
          values={{ feature }}
          components={{ code: <code className="font-mono" /> }}
        />
      </p>
      <p className="m-0 mt-2 text-sm text-ink-muted">{t('gatedRoute.upgradeRefusal.planHint')}</p>
    </Panel>
  );
}

export function GatedRoute({
  title,
  capability,
  feature,
  children,
  helpTopicId,
}: {
  readonly title: string;
  readonly capability: Capability | null;
  readonly feature: Feature | null;
  readonly children: ReactNode;
  /** Optional, since not every gated page has one yet — `GatedPages.tsx`'s `AnalyticsPage` has none. */
  readonly helpTopicId?: string;
}) {
  const grants = useGrants();
  const session = useSession();

  // Capability first, matching `shell/navigation.ts`. A role with no business here must not be
  // offered an upgrade that would not help.
  const refusal =
    capability !== null && !decide(grants, capability) ? (
      <PermissionRefusal capability={capability} />
    ) : feature !== null && !isEntitled(session.tier, feature) ? (
      <UpgradeRefusal feature={feature} />
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      <PageHeading title={title} helpTopicId={helpTopicId} />
      {refusal ?? children}
    </div>
  );
}
